import { useCallback, useEffect, useState } from 'react'
import { addSeconds, differenceInSeconds, format } from 'date-fns'
import { PasswordError } from '../thunks'

const getTimeLeft = (date: Date | null) => {
  if (date === null) {
    return null
  }
  const now = Date.now()
  const timeLeft = differenceInSeconds(date, now)
  if (timeLeft <= 0) {
    return null
  }
  const helperDate = addSeconds(new Date(0), timeLeft)
  return format(helperDate, 'mm:ss')
}

export const useTimedPasswordError = ({
  errors,
  timePassedCallback,
}: {
  errors: { password?: PasswordError }
  timePassedCallback: () => void
}) => {
  const [dateToRetry, setDateToRetry] = useState<Date | null>(null)
  const [timedPasswordMessage, setTimedPasswordMessage] = useState<string>('')
  const hasTimedPasswordError = Boolean(errors.password && errors.password.time)

  useEffect(() => {
    if (!hasTimedPasswordError) {
      setDateToRetry(null)
      return
    }
    const retryAfter = (errors.password && errors.password.time) || 0
    const date = new Date(Date.now() + retryAfter * 1000)
    setDateToRetry(date)
  }, [errors.password, hasTimedPasswordError])

  const timeSetter: () => void = useCallback(() => {
    const timeLeft = getTimeLeft(dateToRetry)
    if (timeLeft === null) {
      timePassedCallback()
      setDateToRetry(null)
      setTimedPasswordMessage('')
    } else {
      setTimeout(timeSetter, 1000)
      const passwordError = errors.password as PasswordError
      setTimedPasswordMessage(passwordError.message.replace('{time}', timeLeft))
    }
    // Exhaustive deps rule adds timeSetter as a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateToRetry, timePassedCallback])

  useEffect(() => {
    if (dateToRetry === null) {
      return
    }
    timeSetter()
  }, [dateToRetry, timeSetter])

  return { timedPasswordMessage, hasTimedPasswordError }
}
